import {
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ListBox from 'src/components/ListBox/ListBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { useTranslate } from 'src/localization/Trns'
import { OrgExpenseReportCycleModel } from 'src/models/orgExpenseReports.models'
import { setOrgExpenseReportCycleUrl } from 'src/navigation/urls'
import {
  issuerSortedRCNOrgExpenseReportCycles,
  useQueryOrgExpenseReportCycles,
} from 'src/queries/orgExpenseReports.queries'
import { css } from 'styled-components'

interface OrgExpenseReportSettingsBoxProps {}

const OrgExpenseReportSettingsBox: React.FC<
  OrgExpenseReportSettingsBoxProps
> = (): React.ReactElement => {
  const translate = useTranslate()

  const { data: cycleGroups, isLoading: loading } =
    useQueryOrgExpenseReportCycles({
      select: issuerSortedRCNOrgExpenseReportCycles,
    })

  const renderPage = (): React.ReactElement => (
    <Box className={'flex-column justify-between gap-5'} noShadow outlineBorder>
      <span
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          "Below, you'll find your registered accounts. Simply input the date from your bank statement to generate statements automatically. Owners and Admins have the flexibility to update these dates anytime.",
        )}
      </span>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        {cycleGroups?.map((rcn: OrgExpenseReportCycleModel) => (
          <ListBox
            key={rcn.id}
            cells={[
              {
                isTitle: true,
                label: translate('Account Name'),
                content: (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      gap: 8px;
                    `}
                  >
                    <span
                      id="title"
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      <span
                        css={css`
                          padding-inline-end: 8px;
                        `}
                      >
                        {rcn.accountName}
                      </span>
                      <Tag
                        text={rcn.accountLast4?.toString()}
                        size={TagSizes.Medium}
                      />
                    </span>
                  </div>
                ),
              },
              {
                label: translate('Statement End Date'),
                content: (
                  <HoverableLink
                    to={setOrgExpenseReportCycleUrl(rcn.id)}
                    ariaLabel={'Set Cycle End Date'}
                    underline
                    regular
                  >
                    {rcn.cycleLabel}
                  </HoverableLink>
                ),
              },
            ]}
          />
        ))}
      </div>
    </Box>
  )

  return loading ? (
    <LoadingBackgroundPage />
  ) : cycleGroups && cycleGroups.length <= 0 ? (
    <TableEmptyState
      design={TableEmptyStateDesigns.OrgExpenseReportCycleSettings}
    />
  ) : (
    renderPage()
  )
}

export default OrgExpenseReportSettingsBox
